/* eslint-disable @nx/enforce-module-boundaries */
import {
    transformResponseDetailsCastCrew,
    transformMpxAllAssetsResponseDetails,
    transformSeasonsResponse,
    transformResponseList,
    transformVOD,
    transformProgram,
} from '../../utils/transformResponse/mpxResponses';
import { container } from '@intigral/domain/lib/DataDomain';
import { DI } from '@intigral/constant/config/DIContainer';
import { IMpxService } from '@intigral/domain/lib/application/services/MpxService';
import { IMpxAllAssetsParams } from '@intigral/domain/lib/entity/interface/iMpxVodResponse';
import { IApiPathManager } from '@intigral/domain/lib/infrastructures/config/ApiPathManager';
import { API_IDS } from '@intigral/constant';
import { IThinkAnalyticsSearchService } from '@intigral/domain/lib/application/services/ThinkAnalyticsSearchService';
import user from '../../helpers/user';
import h, {
    redirectToProfileSelection,
    getCountry,
    getProfileRating,
    getAccumulatedSubscriptionGuids,
} from '../../helpers/common';
import channelsUtil from '../../helpers/channelsUtil';
import { __ } from '../../translations';

export const getMpxCastCrewApi = async (byId) => {
    const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);
    const response = await mpxService.getMpxCastCrew(`byProgramId=${byId}`);
    if (response && response.entries) {
        return transformResponseDetailsCastCrew(response);
    } else {
        return response;
    }
};

export const getMpxAllAssetsApi = async (params: IMpxAllAssetsParams) => {
    const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);
    const response = await mpxService.getMpxAllAssets(params);
    if (response && response.entries) {
        return transformMpxAllAssetsResponseDetails(response);
    } else {
        return response;
    }
};

export const getSeasonsApi = async (bySeriesId, byCustomValue) => {
    const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);

    const response = await mpxService.getSeasons(bySeriesId, byCustomValue);
    if (response && response.entries) {
        return transformSeasonsResponse(response);
    } else {
        return response;
    }
};

export const getEpisodesApi = async (byTvSeasonId) => {
    const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);
    const response = await mpxService.getEpisodes(byTvSeasonId, '');
    if (response && response.entries) {
        return transformResponseList(response);
    } else {
        return response;
    }
};

export const getCatchUpEpisodesApi = async (bySeriesId, callSign) => {
    const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);
    const response = await mpxService.getCatchUpEpisodeList({ bySeriesId, callSign });
    if (response && response.entries) {
        return transformResponseList(response);
    } else {
        return response;
    }
};

export const getMixedCatchUpEpisodesApi = async (bySeriesId) => {
    const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);
    const response = await mpxService.getMixedCatchUpEpisodeList({ bySeriesId });
    if (response && response.entries) {
        return transformResponseList(response);
    } else {
        return response;
    }
};

export const getVodListApi = async (stream: string, params) => {
    try {
        const apiPathManager = container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
        const url = apiPathManager.getPath(API_IDS.MPX_ALL_VOD_FEED) ?? '';
        // const url = apiPathManager.replacePlaceholders(filter_params);

        const response = await container.resolve<IMpxService>(DI.SERVICES.IMpxService).getFeed(url, params);
        if (response && response?.entries) {
            return transformResponseList(response);
        } else {
            return [];
        }
    } catch (error) {
        return [];
    }
};

export const getEmptyResult = () => {
    return new Promise((resolve, reject) => {
        resolve({});
    });
};

export const getVODAndLive = (query, language = 'en') => {
    const searchParams = {
        profileName: user.getProfileName(),
        userName: user.getUsername(),
        device: h.getDevice(),
        country: getCountry(),
        subGuids: getAccumulatedSubscriptionGuids(),
        rating: getProfileRating(),
        apiKey: user.getAPIKey(),
        q: query,
        lang: language.toUpperCase(),
        range: h.getDefaultRange(),
    };

    if (user.getProfileMode() === 'kid') {
        searchParams['categoryType'] = 'kids';
    }
    const taService = container.resolve<IThinkAnalyticsSearchService>(DI.SERVICES.IThinkAnalyticsSearchService);
    return taService
        .searchPA(searchParams, user.getIdentityToken())
        .then((response) => {
            // checking in case of invalid identityToken
            if (
                response &&
                'description' in response &&
                (response.description as string).match(/[iI]nvalid [iI]dentity[tT]oken/i)
            ) {
                redirectToProfileSelection('9001');
            }
            if (response && response?.data?.entries) {
                return { entries: response.data.entries };
            } else {
                return response;
            }
        })
        .catch((_error) => {
            return _error;
        });
};

export const getSearchAllApi = async (query, language = h.getLang(), param = {}) => {
    const result = {
        recommendations: [],
        vod: [],
        live: [],
        rewind: [],
        person: [],
        all: [],
    };

    return getVODAndLive(query, language)
        .then((values) => {
            const vodAndLiveItems =
                    (values && values?.entries && Array.isArray(values.entries) && values.entries) || [],
                recommendedContent =
                    (values &&
                        values?.recommendedContent &&
                        Array.isArray(values.recommendedContent) &&
                        values.recommendedContent) ||
                    [];
            const alreadyAdded = [];
            if (vodAndLiveItems.length > 0) {
                const now = Date.now();
                vodAndLiveItems.forEach((item) => {
                    if (item.assetType === 'VOD') {
                        const vodItem = { ...transformVOD(item), type: 'VOD' };
                        result.vod.push(vodItem);
                        result.all.push(vodItem);
                    } else if (item.assetType === 'LINEAR') {
                        const rewindItem = { ...transformProgram(item), type: 'LIVETV' };
                        if (!alreadyAdded.includes(item.guid)) {
                            if (
                                new Date(Number(rewindItem.startTime)).getTime() <= now &&
                                now < new Date(Number(rewindItem.endTime)).getTime()
                            ) {
                                result.live.push(rewindItem);
                            } else {
                                if (new Date(Number(rewindItem.startTime)).getTime() <= now) {
                                    result.rewind.push(rewindItem);
                                }
                            }
                        }
                        // result.all.push(rewindItem);
                    } else if (item.assetType !== 'LINEAR') {
                        if (!alreadyAdded.includes(item.guid)) {
                            if (channelsUtil.isNow(item)) {
                                const liveTvItem = { ...transformProgram(item), type: 'LIVETV' };
                                result.live.push(liveTvItem);
                                result.all.push(liveTvItem);
                                alreadyAdded.push(item.guid);
                            }
                        }
                    }
                    alreadyAdded.push(item.guid);
                });
            } else if (recommendedContent.length > 0) {
                recommendedContent.sort((a, b) => (a.order > b.order ? 1 : -1));
                result.recommendations = recommendedContent.map((item) => {
                    return {
                        order: item.order,
                        meta: {
                            title: {
                                en: __(`search_recemmendation_${item.title}`, { lng: 'en' }),
                                ar: __(`search_recemmendation_${item.title}`, { lng: 'ar' }),
                            },
                        },
                        content: item.entries.map((vod) => transformVOD(vod)),
                    };
                });
            }
            return result;
        })
        .catch((error) => {
            throw error;
        });
};
